<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import { onDestroy, onMount } from 'svelte';

	import {
		AddPrivacySolution,
		BlockColdSolution,
		BlockHeatSolution,
		BlockLightSolution,
		BlockNoiseSolution,
		BlockUVSolution,
	} from '$lib/data/solutions/solutions-data';

	let { id, class: className } = $props();

	const solutions = [
		BlockColdSolution,
		BlockNoiseSolution,
		BlockUVSolution,
		BlockHeatSolution,
		BlockLightSolution,
		AddPrivacySolution,
	];

	let rotating = $state(true);
	let container = $state();
	let currentSlide = $state('image-0');
	let scrollingToSlide = $state(false);
	let resetTimer = $state();
	let rotateTimer = $state();
	let observer = $state();

	onMount(() => {
		clearTimeout(rotateTimer);
		rotateTimer = setTimeout(() => rotate(), 2000);

		// set up an intersection observer for the image slider
		observer = new IntersectionObserver(handleSlideChange, {
			root: container.parentElement,
			delay: 100,
			threshold: 0.1,
		});

		// loop through the slides and register them with the observer
		for (const slide of container.children) {
			observer.observe(slide);
		}
	});

	onDestroy(() => {
		clearTimeout(rotateTimer);
		clearTimeout(resetTimer);
		observer?.disconnect();
	});

	function handleSlideChange(slides) {
		slides.forEach((slide) => {
			let slideIndex = Number(slide.target.id.split('-')[1]);
			if (scrollingToSlide !== null && scrollingToSlide !== slideIndex) {
				return;
			}
			if (slide.isIntersecting) {
				currentSlide = slide.target.id;
				scrollingToSlide = null;
			}
		});
	}

	function rotate() {
		if (!rotating || !container) {
			return;
		}
		let nextSlideIndex =
			currentSlide != null
				? (Number(currentSlide?.split('-')[1]) + 1) % solutions.length
				: 0;
		scrollToSlideByIndex(nextSlideIndex);
		clearTimeout(rotateTimer);
		rotateTimer = setTimeout(() => rotate(), 5000);
	}

	function scrollToSlideByIndex(index) {
		if (isNaN(index) || !container) {
			return;
		}

		// keep track of the slide we're scrolling to so we don't show intermediate states
		scrollingToSlide = index;

		container.scrollTo({
			left:
				container.children[index].offsetLeft -
				container.children[index].offsetWidth / 3,
			behavior: 'smooth',
		});
	}

	function setResetTimer() {
		clearTimeout(resetTimer);
		resetTimer = setTimeout(() => {
			rotating = true;
			rotate();
		}, 10000);
	}
</script>

<div
	class={[className, 'col-span-full! mb-8 w-full max-w-2xl overflow-hidden']}
>
	<div {id} class="col-span-full! w-full max-w-2xl overflow-hidden">
		<!-- eslint-disable better-tailwindcss/no-unknown-classes -->
		<ul
			bind:this={container}
			class="
     scroll-container mb-4 flex w-full snap-x snap-mandatory list-none divide-x
     overflow-x-scroll select-none
     md:overflow-x-hidden
   "
			style="scroll-snap-align: start;"
			ontouchmove={() => {
				rotating = false;
				setResetTimer();
			}}
			onwheel={(e) => {
				if (Math.abs(e.deltaX) > 0) {
					rotating = false;
				}
			}}
			onmouseleave={() => {
				if (rotating) {
					return;
				}
				setResetTimer();
			}}
		>
			<!-- eslint-enable better-tailwindcss/no-unknown-classes -->
			{#each solutions as { graphic, title }, index (index)}
				<li class="w-full shrink-0 snap-start border-0" id={`image-${index}`}>
					<Image
						class="w-full px-4"
						src={graphic}
						alt={title}
						loading={index === 0 ? 'eager' : 'lazy'}
					/>

					<p
						class="
        text-center font-medium text-indow-grey-medium uppercase
        md:hidden
      "
					>
						{title}
					</p>
				</li>
			{/each}
		</ul>
	</div>
	<div class="content-grid">
		<ul
			class="
     group grid list-none grid-cols-2 gap-4
     md:grid-cols-3
   "
			onmouseleave={() => {
				if (rotating) {
					return;
				}
				setResetTimer();
			}}
		>
			{#each solutions as { href, title }, index (index)}
				<li class="flex items-center justify-center">
					<a
						onmouseenter={() => {
							rotating = false;
							currentSlide = `image-${index}`;
							scrollToSlideByIndex(index);
						}}
						{href}
						class={[
							{
								'bg-indow-white text-indow-blue':
									currentSlide?.split('-')[1] === index.toString(),
							},
							`
         flex size-full cursor-pointer items-center justify-center
         place-self-center rounded-sm border-3 border-indow-blue bg-indow-blue
         p-3 text-center font-medium uppercase no-underline transition-all
         duration-200 ease-in-out select-none
         hover:bg-indow-white hover:text-indow-blue
       `,
						]}>{title}</a
					>
				</li>
			{/each}
		</ul>
	</div>
</div>

<style>
	.scroll-container {
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* IE and Edge */
	}

	.scroll-container::-webkit-scrollbar {
		display: none; /* Chrome, Safari, Opera */
	}
</style>
